import {
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
} from '@mui/material'

import { Fragment, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import WTypography from 'Components/whiteTypography'
import urls from 'utils/urls'
import { makePostRequest } from 'utils/api'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'

import AddCircleIcon from '@mui/icons-material/AddCircle'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import EmptyCardState from './EmptyCardState'
import { Input } from 'Components/Input'
import { useForm } from 'react-hook-form'
import ComboBox from 'Components/ComboBox'
import { makeGetRequest } from 'utils/api'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

// LAP-PAT1 — libellé « mois AAAA » d'une date ISO projetée, selon la locale.
function formatProjectedMonth(iso, locale) {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleDateString(locale || 'fr-FR', {
      month: 'long',
      year: 'numeric',
    })
  } catch (e) {
    return iso
  }
}

// LAP-PAT1 — barre de progression + date projetée + écart vs échéance.
function ObjectiveProjection({ projection, t, locale }) {
  if (!projection) return null
  const { progress_pct, projected_date, gap_months } = projection
  const projectedLabel = formatProjectedMonth(projected_date, locale)
  let gapLabel = null
  let gapColor = '#8be28b'
  if (gap_months != null) {
    if (gap_months > 0) {
      gapLabel = `+${gap_months} ${t('monPatrimoine.projectionMonthsLate')}`
      gapColor = '#ffb36b'
    } else if (gap_months < 0) {
      gapLabel = `${Math.abs(gap_months)} ${t('monPatrimoine.projectionMonthsEarly')}`
    } else {
      gapLabel = t('monPatrimoine.projectionOnTime')
    }
  }
  return (
    <Box sx={{ px: '4px', pb: '6px' }}>
      {progress_pct != null && (
        <Box
          sx={{
            height: '5px',
            borderRadius: '3px',
            backgroundColor: '#ffffff1f',
            overflow: 'hidden',
            mb: '3px',
          }}
        >
          <Box
            sx={{
              width: `${Math.min(progress_pct, 100)}%`,
              height: '100%',
              borderRadius: '3px',
              backgroundColor: progress_pct >= 100 ? '#8be28b' : '#6087cd',
            }}
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <WTypography sx={{ fontSize: '11px', color: '#ffffffa1' }}>
          {progress_pct != null ? `${progress_pct}%` : ''}
          {projectedLabel
            ? ` · ${t('monPatrimoine.projectionReachedIn')} ${projectedLabel}`
            : ''}
        </WTypography>
        {gapLabel && (
          <WTypography sx={{ fontSize: '11px', color: gapColor }}>{gapLabel}</WTypography>
        )}
      </Box>
    </Box>
  )
}

function MesObjectifsCard({ oldData, updateReports }) {
  const { t, i18n } = useTranslation('layout') // LAP-733 (GE022)
  const navigate = useNavigate()
  const [selected, setSelected] = useState([])
  const [addOpen, setAddOpen] = useState(false)

  // Carte vide : aucun objectif patrimonial enregistré.
  const hasObjectives = Array.isArray(oldData) && oldData.length > 0

  const deleteBulkOperations = async () => {
    if (selected.length === 0) {
      alert(t('monPatrimoine.selectOneOperation')) // LAP-733 (GE022)
    } else {
      const response = await makePostRequest(
        urls.deleteUserObjectIfs,
        { selected: selected },
        {},
        true,
      )
      if (response.status === 200) {
        console.log('updated Successfully')
        updateReports()
      }
    }
  }
  return (
    <>
      <Card className="dashboard_card__root1">
        {/* Header cliquable → application Mes Objectifs */}
        <Box
          className="dashboard_card__header1 no-drag"
          onClick={() => navigate('/mesobjectifs')}
          sx={{ cursor: 'pointer' }}
        >
          <WTypography component={'span'} variant="h6">
            {t('monPatrimoine.myObjectives')}
          </WTypography>
        </Box>

        <Box className="dashboard_card__header1">
          <WTypography sx={{ color: '#ffffffa1' }} variant={'body2'}>
            {t('monPatrimoine.patrimonyObjectives')}
          </WTypography>
          <Box className="no-drag">
            {/* Le formulaire reste monté pour que l'état vide puisse aussi
                ouvrir la boîte de dialogue ; l'icône « + » de l'en-tête n'est
                affichée que lorsqu'il y a déjà des objectifs. */}
            <AddObjectForm
              updateReports={updateReports}
              open={addOpen}
              setOpen={setAddOpen}
              showTrigger={hasObjectives}
            />
            {hasObjectives && (
              <IconButton onClick={deleteBulkOperations}>
                <RemoveCircleIcon sx={{ color: 'gray' }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {hasObjectives ? (
          <Box mt={0} minWidth={3} mb={6} height={'85%'} sx={{ overflowX: 'auto' }}>
            <Box className="no-drag">
              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: 'unset',
                  bgcolor: 'unset',
                  '*': { color: 'white !important' },
                  pr: '6px',
                }}
              >
                <Table sx={{}} aria-label="spanning table">
                  <TableBody
                    sx={{
                      '.MuiTableCell-root': {
                        padding: '0px',
                      },
                    }}
                  >
                    {oldData?.map((el) => (
                      <Fragment key={el.id}>
                        <TableRow>
                          <TableCell colSpan={2} sx={{ borderBottom: el.projection ? 'none' : undefined }}>
                            <FormControlLabel
                              className="ficheck"
                              control={
                                <Checkbox
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelected([...selected, el.id])
                                    } else {
                                      setSelected([...selected.filter((c) => c !== el.id)])
                                    }
                                  }}
                                />
                              }
                              label={el.objectives_name || el.objectives_name_manual}
                            />
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={{ borderBottom: el.projection ? 'none' : undefined }}
                          >
                            {el.objectifs}
                            {''}
                            {el.unit_of_measurement}
                          </TableCell>
                        </TableRow>
                        {/* LAP-PAT1 — projection datée de l'objectif financier */}
                        {el.projection && (
                          <TableRow key={`${el.id}-projection`}>
                            <TableCell colSpan={3}>
                              <ObjectiveProjection
                                projection={el.projection}
                                t={t}
                                locale={i18n.language}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        ) : (
          <EmptyCardState
            icon={<TrackChangesIcon />}
            title={t('monPatrimoine.objectivesEmptyTitle')}
            description={t('monPatrimoine.objectivesEmptyDesc')}
            ctaLabel={t('monPatrimoine.objectivesEmptyCta')}
            onCta={() => setAddOpen(true)}
          />
        )}
      </Card>
    </>
  )
}

export default MesObjectifsCard

function AddObjectForm({ updateReports, open, setOpen, showTrigger = true }) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [selectedDomain, setSelectedDomain] = useState(null)
  const [uniteM, setUniteM] = useState('')
  const [objectifsMetadata, setObjectifsMetaDeta] = useState({
    domains: [],
    frequency: [],
    predefinedObjectives: [],
    status: [],
  })

  const { handleSubmit, reset, setValue, control } = useForm({
    domain: null,
    objectives_name: null,
    unit_of_measurement: null,
    objectifs: null,
    periodicity: null,
    date_objectives: null,
  })

  useEffect(() => {
    fetchMesObjectifsMetaData()
  }, [])

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    reset()
    setUniteM('')
  }

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      domain: data.domain.id,
      objectives_name: data.objectives_name.id,
      periodicity: data.periodicity.id,
    }
    const response = await makePostRequest(urls.crudMesObjectifs, payload, {}, true)
    if (response.status === 201) {
      updateReports()
      handleClose()
      setOpen(false)
    }
  }
  const fetchMesObjectifsMetaData = async () => {
    const response = await makeGetRequest(urls.fetchMesObjectifsMetaData, {}, true)
    if (response.status === 200) {
      const domains = []
      const frequency = []
      const predefinedObjectives = []
      const status = [{ label: 'En cours' }, { label: 'Annulé' }, { label: 'reussite' }]
      response.data.domains.forEach((v) => {
        if (v.domain === 'Finance') {
          setSelectedDomain({ label: v.domain, id: v.id })
          setValue('domain', { label: v.domain, id: v.id })
        }
        domains.push({ label: v.domain, id: v.id })
      })
      response.data.frequency.forEach((v) => {
        frequency.push({ label: v.name_of_periodicity, id: v.id })
      })
      response.data.predefined_objectives.forEach((v) => {
        predefinedObjectives.push({
          label: v.objectives_name,
          id: v.id,
          domain: v.domain,
          value: v.value_format,
        })
      })
      setObjectifsMetaDeta({
        domains,
        frequency,
        predefinedObjectives,
        status,
      })
    }
  }
  return (
    <>
      {showTrigger && (
        <IconButton onClick={handleClickOpen}>
          <AddCircleIcon sx={{ color: 'gray' }} />
        </IconButton>
      )}
      <Dialog fullWidth maxWidth="xs" open={open} onClose={handleClose} className="no-drag">
        <DialogTitle sx={{ color: '#383838 !important' }}>{t('monPatrimoine.addObjectiveDialogTitle')}</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <ComboBox
              handleAutoComplete={(e, j) => setValue('domain', j)}
              label={t('monPatrimoine.domain')}
              data={objectifsMetadata.domains}
              disabled
              name="domain"
              required={true}
              selectedValue={selectedDomain}
            />
            <ComboBox
              handleAutoComplete={(e, j) => {
                setValue('objectives_name', j)
                setUniteM(j.value)
                setValue('unit_of_measurement', j.value)
              }}
              label={t('monPatrimoine.objectives')}
              data={objectifsMetadata.predefinedObjectives}
              name="objectives_name"
              required={true}
            />
            <Input
              control={control}
              inputProps={{
                variant: 'outlined',
                required: true,
                size: 'small',
                type: 'text',
              }}
              rules={{ required: true }}
              value={uniteM}
              name="unit_of_measurement"
              label={t('monPatrimoine.unitOfMeasure')}
            />
            <Input
              control={control}
              inputProps={{
                variant: 'outlined',
                required: true,
                size: 'small',
                type: 'text',
              }}
              rules={{ required: true }}
              name="objectifs"
              label={t('monPatrimoine.value')}
            />
            <Input
              control={control}
              inputProps={{
                variant: 'outlined',
                required: true,
                size: 'small',
                type: 'date',
              }}
              InputLabelProps={{ shrink: true }}
              rules={{ required: true }}
              name="date_objectives"
              label={t('monPatrimoine.dueDate')}
            />{' '}
            <ComboBox
              handleAutoComplete={(e, j) => setValue('periodicity', j)}
              label={t('monPatrimoine.periodicity')}
              data={objectifsMetadata.frequency}
              name="periodicity"
              required={true}
            />
          </DialogContent>
          <DialogActions>
            <Button variant="contained" type="submit">
              {t('monPatrimoine.validate')}
            </Button>
            <Button onClick={handleClose}>{t('monPatrimoine.cancel')}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}
